import { useEffect, useState } from "react";
import api, { inr } from "@/lib/api";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const FULFILL = ["processing", "packed", "shipped", "delivered", "cancelled"];
const PAY = ["pending", "paid", "cod_pending", "failed"];

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [active, setActive] = useState(null);

  const load = () => api.get("/admin/orders").then(({ data }) => setOrders(data));
  useEffect(() => { load(); }, []);

  const update = async (id, field, value) => {
    await api.put(`/admin/orders/${id}/status`, { [field]: value });
    toast.success("Order updated");
    load();
    if (active?.id === id) setActive({ ...active, [field]: value });
  };

  const badge = (s) => s === "paid" ? "bg-[var(--emerald)] text-[#FBF7DE]" : s === "failed" || s === "cancelled" ? "bg-[var(--maroon)] text-[#FBF7DE]" : "bg-[#F3EECB] text-[var(--bronze)]";

  return (
    <div data-testid="admin-orders">
      <h1 className="font-serif text-4xl text-[var(--bronze)] mb-2">Orders</h1>
      <p className="text-[var(--olive)] mb-8">{orders.length} orders received</p>

      {orders.length === 0 ? <div className="border border-[var(--line)] p-12 text-center text-[var(--olive)]">No orders yet.</div> : (
        <div className="border border-[var(--line)] overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-[#F3EECB]"><tr className="uplabel text-[0.6rem] text-left">
              <th className="p-4">Order</th><th className="p-4">Customer</th><th className="p-4">Total</th><th className="p-4">Payment</th><th className="p-4">Fulfillment</th><th className="p-4"></th>
            </tr></thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} data-testid={`admin-order-${o.id}`} className="border-t border-[var(--line)]">
                  <td className="p-4"><div className="text-[var(--bronze)] font-medium">{o.order_number}</div><div className="text-xs text-[var(--olive)]">{new Date(o.created_at).toLocaleDateString("en-IN")}</div></td>
                  <td className="p-4"><div className="text-[var(--bronze)]">{o.address?.full_name}</div><div className="text-xs text-[var(--olive)]">{o.email}</div></td>
                  <td className="p-4 text-[var(--bronze)]">{inr(o.total)}<div className="text-xs text-[var(--olive)] uppercase">{o.payment_method}</div></td>
                  <td className="p-4">
                    <Select value={o.payment_status} onValueChange={(v) => update(o.id, "payment_status", v)}>
                      <SelectTrigger className={`h-8 text-xs border-none ${badge(o.payment_status)}`}><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#FBF7DE] border-[var(--line)]">{PAY.map((s) => <SelectItem key={s} value={s} className="text-xs">{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                  <td className="p-4">
                    <Select value={o.fulfillment_status} onValueChange={(v) => update(o.id, "fulfillment_status", v)}>
                      <SelectTrigger data-testid={`fulfillment-select-${o.id}`} className="h-8 text-xs border-[var(--line)] bg-transparent text-[var(--bronze)]"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#FBF7DE] border-[var(--line)]">{FULFILL.map((s) => <SelectItem key={s} value={s} className="text-xs capitalize">{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                  <td className="p-4"><button onClick={() => setActive(o)} className="uplabel text-[var(--olive)] hover:text-[var(--bronze)]">View</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!active} onOpenChange={(v) => !v && setActive(null)}>
        <DialogContent className="bg-[#FBF7DE] border-[var(--line)] max-w-lg max-h-[90vh] overflow-y-auto">
          {active && <>
            <DialogHeader><DialogTitle className="font-serif text-2xl text-[var(--bronze)]">{active.order_number}</DialogTitle></DialogHeader>
            <div className="space-y-4 text-sm">
              <div className="bg-[#F3EECB] p-4">
                <div className="uplabel text-[0.6rem] mb-2">Delivery Address</div>
                <div className="text-[var(--bronze)]">{active.address.full_name} · {active.address.phone}</div>
                <div className="text-[var(--olive)]">{active.address.line1}, {active.address.line2} {active.address.city}, {active.address.state} — {active.address.pincode}</div>
              </div>
              <div className="space-y-2">
                {active.items.map((it, i) => (
                  <div key={i} className="flex gap-3"><img src={it.image} alt="" className="w-12 aspect-[3/4] object-cover" /><div className="flex-1"><div className="text-[var(--bronze)]">{it.name}</div><div className="text-xs text-[var(--olive)]">Size {it.size} × {it.quantity}</div></div><span className="text-[var(--bronze)]">{inr(it.price * it.quantity)}</span></div>
                ))}
              </div>
              <div className="border-t border-[var(--line)] pt-3 space-y-1">
                <div className="flex justify-between text-[var(--bronze)]"><span>Subtotal</span><span>{inr(active.subtotal)}</span></div>
                {active.discount > 0 && <div className="flex justify-between text-[var(--emerald)]"><span>Discount</span><span>-{inr(active.discount)}</span></div>}
                <div className="flex justify-between text-[var(--bronze)]"><span>Shipping</span><span>{active.shipping === 0 ? "Free" : inr(active.shipping)}</span></div>
                {active.cod_fee > 0 && <div className="flex justify-between text-[var(--bronze)]"><span>COD Fee</span><span>{inr(active.cod_fee)}</span></div>}
                <div className="flex justify-between font-serif text-lg text-[var(--bronze)] pt-2"><span>Total</span><span>{inr(active.total)}</span></div>
              </div>
            </div>
          </>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
